import { FC, useMemo } from 'react'
import { AtendimentoView } from '../../../../atendimento/core/_models'
import {
  Button,
  ButtonIcon,
  FetchState,
  FormDateTimeInput,
  FormSelect,
  IOption,
  Label,
  ListLoading,
  LoadingIndicator,
  useModal,
} from '../../../../../../components'
import { useFormik } from 'formik'
import { atendimentoSchema } from './validation/atendimentoSchema'
import {
  AtendimentoType,
  ButtonType,
  IconPaths,
  IconSize,
  OptionsHelper,
  showAlert,
  showConfirmAlert,
  showError,
} from '../../../../../../../_metronic/helpers'
import { AtendimentoDtoUpdate } from '../../core/_models'
import { updateAtendimento } from '../../core/_requests'
import {
  addFormularioToAtendimento,
  deleteFormularioAtendimento,
} from '../../../../avaliacoes/avaliacoes-list/core/_requests'
import { FormularioAtendimentoDtoAdd } from '../../../../avaliacoes/avaliacoes-list/core/_models'
import { useT } from '_metronic/i18n/Metronici18n'

type IProps = {
  emAtendimento?: boolean
}

export const AtendimentoModal: FC<IProps> = ({ emAtendimento }) => {
  const { refetch } = useModal()
  const { fetchStates, closeModal } = useModal()
  const { t } = useT()

  const { data: atendimento } = (fetchStates['atendimento'] || {}) as FetchState<AtendimentoView>
  const { data: unidades } = (fetchStates['unidades'] || {}) as FetchState<IOption<number>[]>

  const initialValues: {
    especialidade: IOption<number> | undefined
    unidade: IOption<number> | undefined
    formulario: IOption<number> | undefined
    retorno: boolean
    dataAtendimento: string
    tipoAtendimento: AtendimentoType
    emAtendimento: boolean
  } = {
    tipoAtendimento: atendimento?.tipo as AtendimentoType,
    especialidade: atendimento?.especialidade
      ? { label: atendimento.especialidade.nome, value: Number(atendimento.especialidade.id) }
      : undefined,
    unidade: atendimento?.unidade
      ? { label: atendimento.unidade.nome || '', value: Number(atendimento.unidade.id) }
      : undefined,
    retorno: atendimento?.retorno || false,
    dataAtendimento: atendimento?.solicitacao || '',
    formulario: undefined,
    emAtendimento: emAtendimento || false,
  }
  const formik = useFormik({
    validationSchema: atendimentoSchema,
    initialValues,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true)
      try {
        if (emAtendimento && values.formulario) {
          const data: FormularioAtendimentoDtoAdd = {
            formulario: values.formulario.value,
          }

          await addFormularioToAtendimento(Number(atendimento?.id), data)

          await showAlert({
            title: 'Sucesso',
            text: 'Avaliação vinculada com sucesso!',
            icon: 'success',
          })

          refetch('atendimento')
          formik.setFieldValue('formulario', undefined)

          return
        }

        let dataAtendimento = String(values.dataAtendimento).replace('T', ' ')

        if (!dataAtendimento.match(/:\d{2}:\d{2}$/)) {
          dataAtendimento += ':00'
        }

        if (values.formulario) {
          const data: FormularioAtendimentoDtoAdd = {
            formulario: values.formulario.value,
          }
          await addFormularioToAtendimento(Number(atendimento?.id), data)
        }

        const data: AtendimentoDtoUpdate = {
          especialidade: values.especialidade?.value,
          retorno: values.retorno,
          unidade: values.unidade ? values.unidade.value : undefined,
          data: dataAtendimento,
        }
        await updateAtendimento(Number(atendimento?.id), data)
        await showAlert({
          title: 'Sucesso',
          text: 'Atendimento atualizado com sucesso!',
          icon: 'success',
        })
        closeModal()
      } catch (error) {
        showError(error)
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleDeleteFormulario = async (idFormularioAtendimento: number) => {
    try {
      const isConfirmed = await showConfirmAlert()
      if (!isConfirmed) return
      await deleteFormularioAtendimento(Number(atendimento?.id), idFormularioAtendimento)
      refetch('atendimento')
    } catch (error) {
      showError(error)
    }
  }

  const extraParamsEspecialidades = useMemo(
    () => ({ pacienteId: atendimento?.paciente?.id, tipo: atendimento?.tipo }),
    [atendimento?.paciente?.id, atendimento?.tipo]
  )

  const filteredFormularios = useMemo(() => {
    return atendimento?.formularios?.filter((f) => !f.cancelado) || []
  }, [atendimento?.formularios])

  return (
    <form onSubmit={formik.handleSubmit} noValidate>
      {!emAtendimento && (
        <>
          {atendimento?.tipo &&
            atendimento?.tipo !== AtendimentoType.Ocupacional &&
            atendimento?.tipo !== AtendimentoType.Screening && (
              <FormSelect
                formik={formik}
                fetchOptions={OptionsHelper.getOptionsEspecialidadesByPaciente}
                name='especialidade'
                label='Especialidade'
                isRequired={true}
                isDisabled={formik.isSubmitting}
                fetchOptionsParams={extraParamsEspecialidades}
                isClearable={false}
                placeholder='Selecione uma especialidade'
              />
            )}

          <FormDateTimeInput
            formik={formik}
            name='dataAtendimento'
            label='Data Atendimento'
            isDisabled={formik.isSubmitting}
            placeholder='Selecione uma data para o atendimento'
            isRequired={true}
            className='mt-3'
          />
          {unidades && unidades.length > 0 && (
            <FormSelect
              formik={formik}
              options={unidades}
              name='unidade'
              label='Unidade/Setor'
              isDisabled={formik.isSubmitting}
              placeholder='Selecione uma unidade'
              className='mt-3'
            />
          )}
        </>
      )}

      {filteredFormularios.length > 0 && (
        <div className='mt-4'>
          <Label className='mb-3'>Avaliações vinculadas</Label>

          <div className='table-responsive'>
            <table className='table table-row-bordered align-middle'>
              <thead>
                <tr className='fw-bold text-muted'>
                  <th>Nome</th>
                  <th>Situação</th>
                  <th className='text-end'>Ação</th>
                </tr>
              </thead>

              <tbody>
                {filteredFormularios.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <Label>{item.formulario?.nome}</Label>
                    </td>

                    <td>
                      <Label>{item.situacao}</Label>
                    </td>

                    <td className='text-end'>
                      <ButtonIcon
                        icon={IconPaths.Trash}
                        iconSize={IconSize.Small}
                        onClick={() => handleDeleteFormulario(Number(item.id))}
                        className='m-0 p-0'
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <FormSelect
        formik={formik}
        name='formulario'
        label='Avaliação para adicionar'
        fetchOptions={OptionsHelper.getOptionsFormularios}
        placeholder='Selecione uma avaliação'
        className='mt-3'
        isDisabled={formik.isSubmitting}
      />

      <div className='d-flex flex-center flex-row-fluid pt-12'>
        <Button
          type={ButtonType.Reset}
          className='btn-secondary me-3'
          onClick={closeModal}
          disabled={formik.isSubmitting}
        >
          {t('common.cancel')}
        </Button>
        <Button type={ButtonType.Submit} className='btn-primary' disabled={formik.isSubmitting}>
          {formik.isSubmitting ? <LoadingIndicator /> : t('common.save')}
        </Button>
      </div>

      {formik.isSubmitting && <ListLoading />}
    </form>
  )
}
